
import { mapGetters as mapVuexGetters } from 'vuex'
import { corporaMixin } from '@/mixins'
import { PropType, defineComponent } from 'vue'
import { DatasetSet, Dataset } from '@/types/dataset'
import { mapActions } from 'pinia'
import { useDatasetStore, useNotificationStore } from '@/stores'
import { isAxiosError } from 'axios'

export default defineComponent({
  mixins: [corporaMixin],
  props: {
    datasetSet: {
      type: Object as PropType<DatasetSet>,
      required: true
    },
    dataset: {
      type: Object as PropType<Dataset>,
      required: true
    },
    // Used to reset the set name field when the modal is closed
    modalOpen: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    loading: false,
    setName: '',
    updateError: { name: '' }
  }),
  mounted () {
    this.setName = this.datasetSet.name
  },
  computed: {
    ...mapVuexGetters('auth', ['isVerified']),
    hasContribPrivilege () {
      return this.isVerified && this.corpus && this.canWrite(this.corpus)
    },
    hasAdminPrivilege () {
      return this.isVerified && this.corpus && this.canAdmin(this.corpus)
    },
    canUpdate () {
      return this.datasetSet.name !== this.setName && this.hasContribPrivilege && this.dataset.state === 'open'
    },
    canDelete () {
      return this.hasAdminPrivilege && this.dataset.state === 'open' && this.dataset.sets.length > 1
    },
    corpusId () {
      return this.dataset.corpus_id
    },
    updateTitle () {
      if (this.dataset.state !== 'open') return 'Sets can only be edited on open datasets'
      else if (!this.hasContribPrivilege) return "You must be a contributor on the project to edit a dataset's sets"
      else if (this.canUpdate) return 'Update set name'
      return undefined
    },
    deleteTitle () {
      if (this.dataset.state !== 'open') return 'Sets can only be deleted on open datasets'
      else if (!this.hasAdminPrivilege) return 'You must be an administrator on the project to delete dataset sets'
      else if (this.dataset.sets.length <= 1) return 'This is the only set in the dataset and cannot be deleted'
      return 'Delete set'
    }
  },
  methods: {
    ...mapActions(useDatasetStore, ['updateDatasetSet', 'deleteDatasetSet']),
    ...mapActions(useNotificationStore, ['notify']),
    async setUpdate () {
      if (!this.canUpdate) return
      this.loading = true
      try {
        await this.updateDatasetSet(this.dataset.id, this.datasetSet.id, this.setName)
        this.notify({ type: 'success', text: 'Dataset set updated' })
        this.updateError.name = ''
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 400 && err.response.data && 'name' in err.response.data) {
          this.updateError.name = err.response.data.name
        }
      } finally {
        this.loading = false
      }
    },
    async setDelete () {
      this.loading = true
      if (!this.canDelete) return
      // Errors are handled (notification) in the store
      try {
        await this.deleteDatasetSet(this.dataset.id, this.datasetSet.id)
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    modalOpen (newValue) {
      // Reset set name field and errors when the modal is closed
      if (newValue === false) {
        this.setName = this.datasetSet.name
        this.updateError.name = ''
      }
    }
  }
})
